import type { ClientOfferTerms } from "@hakili/contracts";
import { estEtapeClose, type EtapeDossier } from "@hakili/domain-types";
import { coveredMonthsOfExpected, receivedOfExpected, formatMoney, t } from "@hakili/i18n";
import { Button, Card } from "@hakili/ui";
import { Camera, CheckCircle2, Clock, MinusCircle, Upload } from "lucide-react";
import { useRef, useState } from "react";

import {
  depositFailureWords,
  type ClientDocumentView,
  type ClientDocuments,
  type VehicleDocumentView,
} from "../session/documents.js";

/**
 * `round C7`, `C7.1`: **WF-CLI-18's screen, to the drawing** , `hakili-client.jsx`,
 * component `Dossier`, dataset `piecesClient`.
 *
 * **The PATH is not here.** `round CW`, `CW.5` moved it to « Suivi », the
 * drawing's fourth tab; this screen answers the other question , what does he
 * still owe , and `I20` is enforced in one place.
 *
 * **Nothing here is derived that a door derives.** The list the buyer owes is
 * the door's (financing five, cash the identity alone), `manquants` is
 * `P1-DOS-013`'s count, the fee's figures are `payments`' and its state is
 * `geste`'s, the vehicle's pièces are answered beside. The screen counts the
 * rows it prints , « N sur M » is the drawing's own bar , and composes no
 * figure of money.
 *
 * **The civility is said after, and nothing before** (`C7.4`): « Bonjour
 * Madame » or « Bonjour Monsieur » once the chargé has read it off the
 * identity document; until then the header carries no address at all, never
 * a guess and never « Madame, Monsieur ».
 */

/** Round `PAY-P`, `M3`: what the coverage door answered for the file's car, or `null` before it did. */
export interface ControlCoverage {
  readonly city: string | null;
  readonly covered: boolean;
  readonly cabinetName: string | null;
  readonly nextControlDay: { readonly label: string; readonly dayOn: string } | null;
}

/** « Toyota Corolla 2020 » where the year is known, the label alone otherwise (R81: never a guess). */
export const labelWithYear = (dossier: {
  readonly vehicleLabel: string;
  readonly vehicleYear?: number | null;
}): string =>
  dossier.vehicleYear === null || dossier.vehicleYear === undefined
    ? dossier.vehicleLabel
    : `${dossier.vehicleLabel} ${String(dossier.vehicleYear)}`;

export interface DossierView {
  /** `L-1b`, `M5` (c): the offers the banks sent, counted by the door and never named (I8). */
  readonly offersReceived?: number;
  /** Round `L-3`, `M2` (WF-CLI-20 step 2): « Où en est l'étude », counts alone; `null` while no bank is engaged. */
  readonly study?: {
    readonly engaged: number;
    readonly studying: number;
    readonly completionsRequested: number;
    readonly offersReceived: number;
    /** `L-4` (A11): the offers the clock ended or the closure declined, a count. */
    readonly offersEnded: number;
    readonly slaDays: number | null;
  } | null;
  /** Round `L-3`, `M2` (WF-CLI-20 steps 3 and 4): the ONE block, the documents to deposit, no bank; `null` when nothing is asked. */
  readonly completionRequest?: {
    readonly documents: readonly {
      readonly code: string;
      readonly label: string;
      readonly helpText: string | null;
    }[];
  } | null;
  /**
   * `AA-3d` (D-1018-AA2): **« Ce qu'il faut compléter »** , her reworded lines (each tied to one of his pièces, or
   * free) and her note, while the file stands back in `collecte` with a request open; `null` otherwise. Nothing of
   * what led to the return is in it: never the grade, the letter, the bank criteria or the analysis' prose.
   */
  readonly collecteRequest?: {
    readonly items: readonly {
      readonly code: string | null;
      readonly text: string;
      readonly done: boolean;
    }[];
    readonly note: string | null;
  } | null;
  /** R23 (b): the vehicle's year from the catalog; `null` where none , the label alone then. */
  readonly vehicleYear?: number | null;
  readonly controlCoverage?: ControlCoverage | null;
  /**
   * `round C3-A`, `C3-A.2`: **the file's own id.**
   *
   * The view carried a `reference` , `H-2026-117`, what a person quotes , and
   * no id, because every screen until now read the file through `/me/dossiers`
   * and never asked a door about it by name. The payment screen asks THREE
   * (`/fees`, `/mandate`, the quote), and a reference is not a key.
   */
  readonly id: string;
  readonly reference: string;
  readonly vehicleLabel: string;
  /** `C3-A.2`: denormalised at creation (`04` §4.2.4), answered by the door. */
  readonly sellerName: string;
  /** `P1-DOS-010`: the chargé the birth chose; `null` for the exception. */
  readonly ownerName?: string | null;
  /** `L-1`, `M1`: the buyer's own deposit and term, the file's; `null` prints a dash (R81). */
  readonly depositPct?: number | null;
  readonly durationMonths?: number | null;
  /** `C3-A.2`: what the buyer owes at this stage, derived by the door. */
  readonly geste: "fee" | "launch" | null;
  readonly etape: string;
  /** The stage's own id, which is what the stepper is keyed on. */
  readonly stage: string;
  readonly attente: string;
  /**
   * `G3-3`, the founder's decision of 17 October 2026 (WAIT IN COLLECTE): the instant he sent his file while the
   * seller's pièces were not all received. The file stays in `collecte`; the screen says what it waits for.
   */
  readonly sentAt?: string | null;
  /** `CW.5`: the chip's tone, decided by the door beside its sentence. */
  readonly ton: string;
  /** `CW.5`: id AND label. A label is not a key , see `P1-CLW-033`. */
  readonly chemin: readonly { readonly id: string; readonly label: string }[];
  /** `round W-13`, `M1`: the verdict and the figures, as `GET /me/dossiers/{id}` answers them. */
  readonly verdict: "conforme" | "ecartprix" | "nonconforme" | null;
  readonly report: {
    readonly estimatedValueAmount: number;
    readonly askedPriceAmount: number;
    readonly gapPct: number | null;
    /** `PRICE-GAP-DEALER`, M2: the restart threshold (value x 1,10) and the seller's answer, the door's own. */
    readonly ceilingAmount?: number;
    readonly priceAnswer?: "aligne" | "propose" | "maintenu" | "accepte" | null;
  } | null;
  readonly feeClientAmount: number | null;
  readonly refund: { readonly reason: string; readonly orderedOn: string } | null;
  /** `K-2` (resumed): the closure's own code and reason; the refund at the dealer's charge on the seller's breach. */
  readonly closure: {
    readonly code: string | null;
    readonly reason: string | null;
    /** `FX-1b`, `M2`: the day the file closed , `closed_at`, answered on every closed stage. */
    readonly at: string | null;
  } | null;
  readonly breachRefund: { readonly totalAmount: number; readonly confirmedOn: string | null } | null;
  readonly sellerPhone: string | null;
  /** `round C5-2`, `M2`, decision D: the control could not take place , the cabinet's reason; Hakili reprograms. */
  readonly controlPrevented: {
    readonly reason: string;
    readonly detail: string | null;
    readonly at: string;
  } | null;
  /** `C7.4`: `null` until the chargé has read it off the identity document. */
  readonly clientGender?: "f" | "m" | null;
  /** `comptant` or `financement`, the door's word. */
  readonly intention?: string;
  /** Round `L-2`, `M4` (A8): the offer the buyer retained, the door's; `null` until he accepts. */
  readonly retainedOffer?: {
    readonly offerId: string;
    readonly bankName: string;
    readonly circuit: "comite" | "signature" | null;
    readonly amountWarranty: number;
    readonly financesWarranty: boolean;
    /** `CLIENT-OFFER-TERMS` (D-1102-3): the four terms of this offer as the door serves them; absent or `null` on an older answer. */
    readonly terms?: ClientOfferTerms | null;
    /**
     * Round `T-1`, `M3` (WF-CLI-24 steps 4 and 5): the deposit at the accepted offer's REAL
     * amount, the two fees each with its financing, the bank's signature as the door carries
     * it , the pack, the appointment, the documents' names and states; no scan id.
     */
    readonly depositAmount: number | null;
    /** `T-1b`: the loan's vehicle part, 05 §2.6.13's `amount_vehicle` , « Montant financé pour le véhicule » on the money's circuit. */
    readonly amountVehicle: number;
    readonly feeBankAmount: number;
    readonly feeHakiliAmount: number;
    readonly financesBankFee: boolean;
    readonly financesHakiliFee: boolean;
    readonly signature: {
      readonly packSentAt: string | null;
      readonly contractSignedAt: string | null;
      readonly appointment: { readonly agence: string; readonly when: string } | null;
      readonly documents: readonly {
        readonly code: string;
        readonly label: string;
        readonly pageCount: number;
        readonly state: string;
      }[];
      readonly signedCount: number;
    } | null;
  } | null;
  /** Round `L-2`, `M5` (WF-CLI-23): the buyer's decision on an unfinanced warranty; `null` before. */
  readonly warrantySettlement?: "comptant" | "retiree" | null;
  /**
   * Round `T-1b`, `M3` (WF-CLI-26): what remains before the money , the retained bank's
   * conditions as facts and Hakili's fee condition (`null` while no fact exists, T-1c's) ,
   * and how the money circulates, the two transfers on the sale price by the door's quote.
   */
  readonly conditions?: {
    readonly bank: {
      readonly bankName: string;
      readonly conditions: readonly {
        readonly key: string;
        readonly label: string;
        readonly state: string;
        readonly liftedAt: string | null;
      }[];
      readonly liftedCount: number;
      readonly total: number;
    } | null;
    readonly hakiliFeeSettled: boolean | null;
    /** `T-1c`: the fee the retained bank finances , nothing for the buyer to settle before the appointment. */
    readonly hakiliFeeFinanced: boolean;
  } | null;
  /** `K-1`, `M3 (d)`: the days the pending move has been his on a held file; `null` otherwise. */
  readonly waitingOnYouDays?: number | null;
  readonly moneyFlow?: {
    readonly salePriceAmount: number;
    readonly ratePct: number;
    readonly dealerAmount: number;
    readonly hakiliAmount: number;
  } | null;
}

/**
 * Round `T-1c`, `M2` (WF-CLI-24 step 5, WF-CLI-26 step 4): **the buyer's file fee as his own
 * door answers it** , two lines with their recipients, the state, the published means when
 * due, his declaration when he made one. Nothing composed on the screen.
 */
export interface FileFeeView {
  readonly state: "aRegler" | "declare" | "encaisse" | "financeParLaBanque";
  readonly hakiliAmount: number;
  readonly bankAmount: number;
  readonly bankName: string | null;
  readonly means: readonly {
    readonly code: string;
    readonly account: {
      readonly bankName: string;
      readonly accountName: string;
      readonly accountNumber: string;
    } | null;
  }[];
  readonly declaration: { readonly means: string; readonly reference: string; readonly at: string } | null;
  readonly collectedAt: string | null;
}

/**
 * The two lines of « Ce que vous payez à Hakili », each a door's figure and a
 * door's state, composed by the app from `/fees`, `/mandate` and the file.
 */
export interface FeesView {
  readonly verification: { readonly amount: number; readonly settled: boolean } | null;
  /** Financing only , a cash buyer has no frais de dossier (WF-CLI-10 §5). */
  readonly file: {
    readonly amount: number;
    readonly status: "auDeblocage" | "aLaSignature" | "regle";
  } | null;
}

const DASH = t("common.valeur.tiret");

/** « Apport 15 % · 48 mois », each half a dash while the file carries no value. */
const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [hole, value]) => text.split(`{${hole}}`).join(value), t(key));

const fillTerms = (depositPct: number | null, durationMonths: number | null): string =>
  t("client.dossier.conditions")
    .replace(
      "{apport}",
      depositPct === null ? DASH : t("client.dossier.apportPct").replace("{apport}", String(depositPct)),
    )
    .replace(
      "{duree}",
      durationMonths === null
        ? DASH
        : t("client.dossier.dureeMois").replace("{duree}", String(durationMonths)),
    );

/** The client's « pieces » is the api's `collecte`; « past it » is the drawing's `envoye`. */
export const CLIENT_DOCUMENTS_STAGE = "collecte";

/**
 * Past `collecte`: the path the door answers walks up to the current stage,
 * so a path that contains `collecte` while the stage is not it is a file that
 * left it , by the buyer's send or by a terminal branch out of a later stage.
 */
export const isPastDocuments = (dossier: Pick<DossierView, "stage" | "chemin">): boolean =>
  dossier.stage !== CLIENT_DOCUMENTS_STAGE &&
  dossier.chemin.some((step) => step.id === CLIENT_DOCUMENTS_STAGE);

// The codes are the WIRE's (`02` §Piece), so they are keyed as data.
const VEHICLE_LABEL: ReadonlyMap<VehicleDocumentView["code"], string> = new Map([
  ["carteGrise", "client.dossier.vehiculeCarteGrise"],
  ["factureProforma", "client.dossier.vehiculeFactureProforma"],
  ["visiteTechnique", "client.dossier.vehiculeVisiteTechnique"],
  ["expertise", "client.dossier.vehiculeExpertise"],
]);

const FILE_FEE_STATUS: Readonly<Record<NonNullable<FeesView["file"]>["status"], string>> = {
  auDeblocage: "client.dossier.auDeblocage",
  aLaSignature: "client.dossier.aLaSignature",
  regle: "client.dossier.regle",
};

/** `PIECES-COUNT`: the three answers the catalogue words; a pièce asking for more than three offers these three. */
const MONTHS_WORDS: ReadonlyMap<
  number,
  "client.dossier.moisUn" | "client.dossier.moisDeux" | "client.dossier.moisTrois"
> = new Map([
  [1, "client.dossier.moisUn"],
  [2, "client.dossier.moisDeux"],
  [3, "client.dossier.moisTrois"],
]);

function DocumentRow({
  piece,
  sent,
  deposit,
}: {
  readonly piece: ClientDocumentView;
  readonly sent: boolean;
  readonly deposit: (code: string, file: File, declaredMonths?: number) => Promise<void>;
}): React.JSX.Element {
  const [helpOpen, setHelpOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /**
   * `PIECES-COUNT`, the months question: the files a buyer has chosen for a `multi_file` pièce and not yet sent, each with the answer
   * he has given (`null` until he gives it). A pièce counts the MONTHS its files cover (one PDF may hold three bulletins), and only
   * he knows what a file holds, so nothing is guessed: no answer is preselected and the send waits for every one.
   */
  const [pending, setPending] = useState<readonly { readonly file: File; readonly months: number | null }[]>(
    [],
  );
  const input = useRef<HTMLInputElement | null>(null);
  /**
   * `FX-2d`, `M1` (`P1-CLW-069`): **the row's state is the PIÈCE's own status, not the file's stage.**
   *
   * It read `sent || piece.status === "recue"`, and `sent` is `isPastDocuments(dossier)`, true from
   * `analyse` on. So a pièce the chargée REJECTED read as received and offered the buyer neither
   * « Envoyer » nor « Remplacer » nor her reason , while the door accepted his deposit for exactly that
   * row. During the pilot her rejection is her only road back to a buyer who sent the wrong photograph.
   *
   * The word below is the DOOR's, not a second rule: `listForClient` projects
   * `status: awaited(row.status) ? "attendue" : "recue"`, so the client's `attendue` IS the door's
   * `awaited()` , `manquante` or `rejetee` , written in the same projection whose count guards the
   * buyer's own send. Screen and door cannot drift here, because they read one word.
   */
  const received = piece.status === "recue";

  /**
   * **`Lane G`: the founder's « nothing happens », and the line that caused it.**
   *
   * `event.target.files` is a LIVE `FileList` in Chromium and Edge: setting `value = ""` (to let the
   * same file be chosen twice) EMPTIES the list the handler already holds. The handler read
   * `const { files } = event.target`, cleared the input, then tested `files.length === 0` , so on every
   * real browser the choice was thrown away before the ticket door was called, with no state, no word
   * and no error. A jsdom spec hands the handler a plain array and cannot see it; the real file
   * chooser can (`e2e/journey.spec.ts`). So the files are COPIED out first, and only then is the
   * input cleared.
   *
   * `S4-5-MULTI-FILE`: the OS picker's own multi-select, for the two pièces that ask for several
   * documents , uploaded one after another by the SAME door `deposit` already is (the backend
   * APPENDS for a `multi_file` pièce instead of overwriting). Every other pièce reads exactly one.
   */
  const choose = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    const chosen = piece.multiFile ? picked : picked.slice(0, 1);
    if (chosen.length === 0) return;
    if (piece.multiFile) {
      // The files wait for the buyer's answer to « Combien de mois ce fichier contient-il ? »; nothing is sent yet.
      setRefusal(null);
      setPending(chosen.map((file) => ({ file, months: null })));
      return;
    }
    setBusy(true);
    setRefusal(null);
    void (async () => {
      for (const file of chosen) await deposit(piece.code, file);
    })()
      .catch((failure: unknown) => {
        setRefusal(depositFailureWords(failure));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  /**
   * `PIECES-COUNT`: the pending files are sent one after another, each with ITS months, by the same `deposit` door. A file that
   * lands leaves the list; the one the door refuses stays, with the door's own sentence, so it can be answered again.
   */
  const sendPending = (): void => {
    if (pending.some((one) => one.months === null)) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      let rest = pending;
      for (const one of pending) {
        await deposit(piece.code, one.file, one.months ?? undefined);
        rest = rest.slice(1);
        setPending(rest);
      }
    })()
      .catch((failure: unknown) => {
        setRefusal(depositFailureWords(failure));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const monthOptions = Array.from({ length: Math.min(piece.expectedFiles ?? 3, 3) }, (_, index) => index + 1);

  return (
    <li
      data-testid={`dossier-piece-${piece.code}`}
      className={`rounded-2xl border bg-white px-4 py-3.5 ${received ? "border-gray-200" : "border-indigo-200"}`}
    >
      <div className="flex items-center gap-3">
        {received ? (
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" aria-hidden="true" />
        ) : (
          <Upload className="h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900">{piece.label}</p>
          {/**
           * `Lane G` (M4): **no state is silent.** The moment a file is chosen the row says « Envoi en
           * cours… »; when the door has marked the pièce it says « Reçu »; a refusal is the alert
           * below, in the door's or the screen's French words. An awaited row at rest says nothing here
           * , its « Envoyer » and « Demandée par… » are its words.
           */}
          {busy || received ? (
            <p
              role="status"
              className={`mt-0.5 text-xs font-medium ${busy ? "text-indigo-700" : "text-emerald-700"}`}
              data-testid={`dossier-piece-${piece.code}-etat`}
            >
              {busy ? t("client.dossier.envoiEnCours") : t("client.dossier.recu")}
            </p>
          ) : null}
          {/**
           * `CANON-1`: **the count `S4-5-MULTI-FILE` named and did not build.**
           *
           * The finding that round closed left a smaller one standing: the row says « reçue » the
           * moment the FIRST file lands, so a client who has sent two of the three bulletins his
           * pièce asks for reads the same word as a client who has sent all three. The line is the
           * two numbers, said plainly, and it is printed from the moment the pièce exists rather
           * than only once received , « 0 sur 3 reçus » is the honest reading of an empty pièce that
           * asks for three, and it is the state the client is in when he first opens the screen.
           *
           * `expectedFiles` is NULL for every pièce that asks for one document, which is what keeps
           * this line off those rows. The screen compares nothing and decides nothing: the door says
           * whether there is a count to print.
           */}
          {piece.monthsSentence !== undefined && piece.monthsSentence !== null ? (
            // `CLIENT-ONE-TRUTH`: a reading speaks for the pièce, so its sentence replaces the declared count; the screen draws it as served.
            <p className="mt-0.5 text-xs text-gray-500" data-testid={`dossier-piece-${piece.code}-compte`}>
              {piece.monthsSentence}
            </p>
          ) : piece.expectedFiles === null ? null : (
            <p className="mt-0.5 text-xs text-gray-500" data-testid={`dossier-piece-${piece.code}-compte`}>
              {/* `PIECES-COUNT`: a pièce the door counts in months (`coveredMonths` served) reads its months; any other keeps the files. */}
              {piece.coveredMonths === null || piece.coveredMonths === undefined
                ? receivedOfExpected(piece.receivedFiles, piece.expectedFiles)
                : coveredMonthsOfExpected(piece.coveredMonths, piece.expectedFiles)}
            </p>
          )}
          {received ? null : (
            <p className="mt-0.5 text-xs text-gray-500">{t("client.dossier.demandeePar")}</p>
          )}
          {/* `OD-37` R7: a piece the founder retired stays on the files born before, and says so. */}
          {piece.retiredOn === undefined || piece.retiredOn === null ? null : (
            <p className="mt-0.5 text-xs text-gray-500" data-testid={`dossier-piece-${piece.code}-retiree`}>
              {t("client.dossier.pieceRetiree").replace("{date}", piece.retiredOn)}
            </p>
          )}
          {/* The chargé's reason, in his words: the row is awaited again and says why. */}
          {!received && piece.rejectionReason !== null ? (
            <p className="mt-0.5 text-xs text-rose-700" data-testid={`dossier-piece-${piece.code}-rejet`}>
              {t("client.dossier.rejetee")} {piece.rejectionReason}
            </p>
          ) : null}
          <button
            type="button"
            className="mt-0.5 text-xs text-indigo-600"
            onClick={() => {
              setHelpOpen((open) => !open);
            }}
          >
            {helpOpen ? t("client.dossier.masquer") : t("client.dossier.commentFaire")}
          </button>
        </div>
        {/* `SWEEP-AA` (D-1019-3): once the file has gone to the verification a received row shows its status and
            carries NO dead « Remplacer ». The reason is said ONCE for the card, above the list (`W5`), where it is
            true of every row at once. */}
        {received && !sent ? (
          <>
            {/**
             * `FX-2c`, `M1` (`F4`): **« Remplacer », the same three crossings as « Envoyer ».**
             *
             * The founder's decision of 26 September, option A: while the dossier is at `collecte`
             * the pièce is still his to put right. It is the SAME picker and the SAME `deposit` , the
             * ticket, the `PUT`, the confirmation , because a second way to send a document would be
             * a second provenance to keep true.
             *
             * `S4-5-MULTI-FILE`: for the two pièces that ask for several documents, this same control
             * reads « Ajouter un autre fichier » and APPENDS (the backend's own distinction, by
             * `piece.multiFile`) rather than replacing , every other pièce keeps replacing, untouched.
             */}
            <input
              ref={input}
              type="file"
              accept="image/*,application/pdf"
              multiple={piece.multiFile}
              className="hidden"
              data-testid={`dossier-piece-${piece.code}-fichier`}
              onChange={choose}
            />
            {pending.length > 0 ? null : (
              <button
                type="button"
                data-testid={`dossier-piece-${piece.code}-remplacer`}
                disabled={busy}
                onClick={() => {
                  input.current?.click();
                }}
                className="shrink-0 rounded-xl border border-indigo-200 bg-white px-3 py-2 text-xs font-medium text-indigo-700 disabled:opacity-50"
              >
                {busy
                  ? t("client.dossier.envoiEnCours")
                  : piece.multiFile
                    ? t("client.dossier.ajouterFichier")
                    : t("client.dossier.remplacer")}
              </button>
            )}
          </>
        ) : null}
        {received ? null : (
          <>
            {/* The camera or a file: the browser's own picker, `capture` so a
                handset opens its camera first. The file never touches the
                screen's state , it goes to the door's URL and nowhere else.
                `S4-5-MULTI-FILE`: the two pièces that ask for several documents
                admit the OS picker's multi-select, uploaded one after another
                by the same `deposit` door , every other pièce stays single. */}
            <input
              ref={input}
              type="file"
              accept="image/*,application/pdf"
              multiple={piece.multiFile}
              className="hidden"
              data-testid={`dossier-piece-${piece.code}-fichier`}
              onChange={choose}
            />
            {pending.length > 0 ? null : (
              <button
                type="button"
                data-testid={`dossier-piece-${piece.code}-envoyer`}
                disabled={busy}
                onClick={() => {
                  input.current?.click();
                }}
                className="shrink-0 rounded-xl bg-indigo-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
              >
                {busy ? t("client.dossier.envoiEnCours") : t("client.dossier.envoyer")}
              </button>
            )}
          </>
        )}
      </div>
      {pending.length === 0 ? null : (
        <div
          className="mt-3 space-y-3 border-t border-gray-100 pt-3"
          data-testid={`dossier-piece-${piece.code}-mois`}
        >
          {pending.map((one, index) => (
            <fieldset key={`${one.file.name}-${String(index)}`} className="m-0 min-w-0 border-0 p-0">
              <legend className="mb-1 text-xs font-medium text-gray-900">
                <span className="break-all text-gray-500">{one.file.name}</span>
                <span className="block">{t("client.dossier.moisDuFichier")}</span>
              </legend>
              <div className="flex flex-wrap gap-2">
                {monthOptions.map((count) => (
                  <label
                    key={count}
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-900 has-[:checked]:border-indigo-600 has-[:checked]:bg-indigo-50"
                  >
                    <input
                      type="radio"
                      className="accent-indigo-600"
                      name={`mois-${piece.code}-${String(index)}`}
                      checked={one.months === count}
                      disabled={busy}
                      data-testid={`dossier-piece-${piece.code}-mois-${String(index)}-${String(count)}`}
                      onChange={() => {
                        setPending((all) =>
                          all.map((item, at) => (at === index ? { file: item.file, months: count } : item)),
                        );
                      }}
                    />
                    {t(MONTHS_WORDS.get(count) ?? "client.dossier.moisUn")}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={`dossier-piece-${piece.code}-envoyer-fichiers`}
              disabled={busy || pending.some((one) => one.months === null)}
              onClick={sendPending}
              className="rounded-xl bg-indigo-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
            >
              {busy ? t("client.dossier.envoiEnCours") : t("client.dossier.envoyer")}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPending([]);
              }}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-50"
            >
              {t("common.action.annuler")}
            </button>
          </div>
          {!busy && pending.some((one) => one.months === null) ? (
            <p
              className="m-0 text-xs text-gray-500"
              data-testid={`dossier-piece-${piece.code}-mois-consigne`}
            >
              {t("client.dossier.moisConsigne")}
            </p>
          ) : null}
        </div>
      )}
      {refusal === null ? null : (
        <p role="alert" className="mt-2 text-xs text-rose-700">
          {refusal}
        </p>
      )}
      {helpOpen ? (
        <div className="mt-2.5 border-t border-gray-50 pt-2.5">
          {piece.helpText === null ? null : (
            <p className="text-xs leading-relaxed text-gray-600">{piece.helpText}</p>
          )}
          <div className="mt-2 flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2">
            <Camera className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
            <p className="text-xs leading-relaxed text-gray-500">{t("client.dossier.conseilPhoto")}</p>
          </div>
          {/**
           * `S4-5-MULTI-FILE`: the binding finding's own words , the hint was written in the
           * singular for a pièce that asks for several documents. Shown for `bulletinsSalaire` and
           * `relevesBancaires` alone (`piece.multiFile`); every other pièce's hint is unchanged.
           */}
          {piece.multiFile ? (
            <p className="mt-2 text-xs leading-relaxed text-gray-500">
              {t("client.dossier.plusieursFichiers")}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * WF-CLI-20 step 4's block: the label, the document's name, its acceptance
 * criteria when the record has them (05 §3.5's `help_text`), « Envoyer la pièce »
 * by the same door as the six, the footer naming the chargé actually assigned.
 * Two documents asked by two banks: ONE block, two rows, still no bank.
 */
/**
 * `AA-3d2` (D-1018-AA2): **« Ce qu'il faut compléter »** , what the chargée, having sent the file back to the
 * collecte, asks him for: her reworded lines (a tied line shows « Reçu » once its pièce is back), her note, then the
 * ratified sentence verbatim and , directly beneath it , his own « Envoyer mon dossier », the ONE gesture that
 * returns the file to the analysis. Nothing happens on an upload: « réception » is his send.
 */
function CollecteRequestBlock({
  request,
  send,
}: {
  readonly request: NonNullable<DossierView["collecteRequest"]>;
  /** His existing send, drawn directly beneath the sentence; `null` while the visible list is not complete. */
  readonly send: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className="rounded-2xl border border-indigo-200 bg-white px-4 py-4"
      data-testid="dossier-a-completer"
    >
      <h2 className="text-base font-semibold text-gray-900">{t("client.dossier.aCompleterTitre")}</h2>
      {request.items.length === 0 ? null : (
        <ul className="mt-2 flex flex-col gap-2" data-testid="dossier-a-completer-liste">
          {request.items.map((item, index) => (
            <li
              key={`${String(index)}-${item.text}`}
              className="flex items-start gap-2 text-sm leading-relaxed text-gray-900"
              data-testid={
                item.code === null ? "dossier-a-completer-libre" : `dossier-a-completer-${item.code}`
              }
              data-done={item.done ? "oui" : "non"}
            >
              {item.done ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
              ) : (
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              )}
              <span>
                {item.text}{" "}
                {item.code === null ? null : (
                  <span className="font-medium text-gray-600">
                    ({item.done ? t("client.dossier.aCompleterFait") : t("client.dossier.aCompleterAFaire")})
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {request.note === null ? null : (
        <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5" data-testid="dossier-a-completer-note">
          <p className="text-sm font-medium text-gray-700">{t("client.dossier.aCompleterNote")}</p>
          <p className="mt-0.5 text-sm leading-relaxed text-gray-900">{request.note}</p>
        </div>
      )}
      <p className="mt-3 text-sm leading-relaxed text-gray-700" data-testid="dossier-a-completer-reprise">
        {t("client.dossier.aCompleterReprise")}
      </p>
      {send}
    </div>
  );
}

function CompletionBlock({
  documents,
  ownerName,
  deposit,
}: {
  readonly documents: readonly {
    readonly code: string;
    readonly label: string;
    readonly helpText: string | null;
  }[];
  readonly ownerName: string | null;
  readonly deposit: (code: string, file: File) => Promise<void>;
}): React.JSX.Element {
  return (
    <div className="rounded-2xl border border-indigo-200 bg-white px-4 py-4" data-testid="dossier-complement">
      <p className="text-xs font-medium text-indigo-700">{t("client.dossier.complementLabel")}</p>
      {documents.map((document) => (
        <CompletionRow key={document.code} document={document} deposit={deposit} />
      ))}
      <p className="mt-2 text-xs leading-relaxed text-gray-400" data-testid="dossier-complement-pied">
        {ownerName === null || ownerName === ""
          ? t("client.dossier.complementPiedSansNom")
          : fill("client.dossier.complementPied", { charge: ownerName })}
      </p>
    </div>
  );
}

function CompletionRow({
  document,
  deposit,
}: {
  readonly document: { readonly code: string; readonly label: string; readonly helpText: string | null };
  readonly deposit: (code: string, file: File) => Promise<void>;
}): React.JSX.Element {
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  return (
    <div className="mt-1" data-testid={`dossier-complement-${document.code}`}>
      <p className="text-base font-semibold text-gray-900">{document.label}</p>
      <p className="mt-1 text-sm leading-relaxed text-gray-600">
        {document.helpText === null ? "" : `${document.helpText} `}
        {t("client.dossier.complementPasRefus")}
      </p>
      <input
        ref={input}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        data-testid={`dossier-complement-${document.code}-fichier`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file === undefined) return;
          setBusy(true);
          setRefusal(null);
          void deposit(document.code, file)
            .catch((failure: unknown) => {
              setRefusal(depositFailureWords(failure));
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      />
      <button
        type="button"
        data-testid={`dossier-complement-${document.code}-envoyer`}
        disabled={busy}
        onClick={() => {
          input.current?.click();
        }}
        className="mt-3 w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white disabled:opacity-50"
      >
        {busy ? t("client.dossier.envoiEnCours") : t("client.dossier.complementEnvoyer")}
      </button>
      {refusal === null ? null : (
        <p role="alert" className="mt-2 text-xs text-rose-700">
          {refusal}
        </p>
      )}
    </div>
  );
}

export function Dossier({
  dossier,
  documents,
  fees,
  deposit,
  submit,
  deconnecter,
  recommencer,
}: {
  readonly dossier: DossierView | null;
  /** `null` while the door has not answered , the list says so, never « 0 ». */
  readonly documents: ClientDocuments | null;
  readonly fees: FeesView | null;
  readonly deposit: (code: string, file: File, declaredMonths?: number) => Promise<void>;
  readonly submit: () => Promise<void>;
  readonly deconnecter: () => void;
  /**
   * `FX-1`, `M2`: the start the founder's rule asks for beside « Aucun dossier en
   * cours ». The screen is handed the gesture and does not know the route.
   */
  readonly recommencer?: (() => void) | undefined;
}): React.JSX.Element {
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  if (dossier === null) {
    return (
      <Card shape="client" className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold text-gray-900">{t("client.dossier.titre")}</h1>
        <p className="text-sm text-gray-500">{t("client.dossier.aucun")}</p>
        <Button variant="secondary" onClick={deconnecter}>
          {t("client.dossier.deconnexion")}
        </Button>
      </Card>
    );
  }

  /**
   * `FX-1`, `M2` , **the founder's rule of 24 September, on this screen.**
   *
   * A dossier closed sans suite, refused, or completed through payout is no
   * longer active; when there is none, « Mon dossier » says so and offers the
   * start. The last closed file is NOT hidden , « Suivi » keeps it readable with
   * its final stage, its motive and its date , which is why this is a different
   * sentence from `client.dossier.aucun`, the buyer who never had one.
   *
   * The staging sitting of 20 to 22 September read a file closed sans suite here
   * as though it were live, with « Envoyer » offered on four pièces. The doors
   * behind those controls refuse since `M2`; this is the screen's own half.
   *
   * The definition is `02` §2.4.1's `ETAPES_CLOSES`, through `@hakili/domain-types`.
   */
  if (estEtapeClose(dossier.stage as EtapeDossier)) {
    return (
      // `Card` forwards only `children`, `shape` and `className`, so the testid goes on a
      // wrapper rather than widening a component every app shares.
      <div data-testid="dossier-aucun-en-cours">
        <Card shape="client" className="flex flex-col gap-4">
          <h1 className="text-xl font-semibold text-gray-900">{t("client.dossier.titre")}</h1>
          <p className="text-sm font-medium text-gray-900">{t("client.dossier.aucunEnCours")}</p>
          <p className="text-sm text-gray-500">{t("client.dossier.aucunEnCoursDetail")}</p>
          {recommencer === undefined ? null : (
            <Button onClick={recommencer}>{t("client.suivi.voirAutresVehicules")}</Button>
          )}
          <Button variant="secondary" onClick={deconnecter}>
            {t("client.dossier.deconnexion")}
          </Button>
        </Card>
      </div>
    );
  }

  const cash = dossier.intention === "comptant";
  const sent = isPastDocuments(dossier);
  // `G3-3`: he has sent, the seller's pièces are still to come , the file waits in `collecte`, and says so.
  const waitingForSeller =
    !sent && dossier.stage === CLIENT_DOCUMENTS_STAGE && (dossier.sentAt ?? null) !== null;
  const pieces = documents?.pieces ?? [];
  const completion = dossier.completionRequest ?? null;
  // `AA-3d2`: the chargée's request, only at the collecte (the door answers none elsewhere).
  const request = dossier.collecteRequest ?? { items: [], note: null };
  const requestOpen =
    dossier.collecteRequest !== undefined &&
    dossier.collecteRequest !== null &&
    dossier.stage === CLIENT_DOCUMENTS_STAGE;
  /**
   * His own send, ONE control wherever it is drawn: under the progress card as ever, or , while the chargée's request
   * stands , directly beneath the sentence of « Ce qu'il faut compléter ». It appears only when the visible list is
   * complete and the file is exactly at the collecte.
   */
  const sendButton = (inBlock: boolean): React.ReactNode =>
    complete && dossier.stage === CLIENT_DOCUMENTS_STAGE ? (
      <Button
        data-testid="dossier-envoyer-verification"
        inactive={sending}
        className={inBlock ? "mt-3 w-full" : "mt-1 w-full"}
        onClick={() => {
          setSending(true);
          setRefusal(null);
          void submit()
            .catch((failure: unknown) => {
              setRefusal(failure instanceof Error ? failure.message : t("common.error.reseau"));
            })
            .finally(() => {
              setSending(false);
            });
        }}
      >
        {t("client.dossier.envoyerDossier")}
      </Button>
    ) : null;
  /**
   * `FX-2d`, `M1`: the SAME cure as the row's, on the same card. It read `pieces.length` past the
   * collection, so a file at `analyse` carrying her rejection counted « 5 sur 5 » above a row that
   * asked to be sent. The count is the pièces that are received, at every stage, and nothing else.
   */
  const received = pieces.filter((piece) => piece.status === "recue").length;
  // Complete is the DOOR's word: nothing missing by its count.
  const complete = documents !== null && pieces.length > 0 && documents.manquants === 0;
  const ratio = pieces.length === 0 ? 0 : (received / pieces.length) * 100;
  const civility =
    dossier.clientGender === "f"
      ? t("client.dossier.bonjourMadame")
      : dossier.clientGender === "m"
        ? t("client.dossier.bonjourMonsieur")
        : null;

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div className="pt-1">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.dossier.titre")}</h1>
        {civility === null ? null : (
          <p className="mt-1 text-sm text-gray-700" data-testid="dossier-civilite">
            {civility}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-500">
          {labelWithYear(dossier)} · {dossier.sellerName}
        </p>
        {/* `L-1`, `M1` (C3-B's ruling (ii)): his own terms, read from the FILE
            and never from a slider; a dash where the file was born before
            them (R81). A cash file carries none and prints nothing. */}
        {dossier.intention === "comptant" ? null : (
          <p className="mt-0.5 text-sm text-gray-500" data-testid="dossier-conditions">
            {fillTerms(dossier.depositPct ?? null, dossier.durationMonths ?? null)}
          </p>
        )}
        {/* Kept from the build before the drawing: the reference is what a
            buyer quotes on the telephone, and « Suivi » prints it too. */}
        <p className="text-xs text-gray-400">{dossier.reference}</p>
      </div>

      {/* Round `L-3`, `M2` (WF-CLI-20 step 4): the bank's request sits ABOVE everything else ,
          the one thing that blocks. One block whatever the number of banks asking, no bank
          named (I8); the deposit by the buyer's own door returns the state to `banques`. */}
      {completion === null ? null : (
        <CompletionBlock
          documents={completion.documents}
          ownerName={dossier.ownerName ?? null}
          deposit={deposit}
        />
      )}

      {/* `AA-3d2`: what the chargée asks him to complete, above the progress card, with his own send under the
          ratified sentence. The same control as below, drawn once: the card's copy is withheld while this block stands. */}
      {requestOpen ? <CollecteRequestBlock request={request} send={sendButton(true)} /> : null}

      {/* WF-CLI-18 step 2: the progress card, four copies by the file's state. */}
      <Card shape="client" className="flex flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{t("client.dossier.piecesRecues")}</span>
          <span className="font-semibold tabular-nums" data-testid="dossier-progres">
            {documents === null
              ? "-"
              : `${String(received)} ${t("client.dossier.sur")} ${String(pieces.length)}`}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-gray-100">
          <div
            className={`h-full rounded-full ${complete || sent ? "bg-emerald-400" : "bg-indigo-500"}`}
            style={{ width: `${String(ratio)}%` }}
          />
        </div>
        <p className="text-xs leading-relaxed text-gray-500" data-testid="dossier-progres-copie">
          {/* R-A (the architect's ruling on `L-3`): WF-CLI-18 step 2's four copies, and a fifth by
              record while a bank's request stands open above , never « plus rien à envoyer » on the
              one screen where he has something to send; the fourth returns once the document is received. */}
          {/* `FX-2d`, `M1`: « sent » becomes « sent AND nothing awaited », `03` §WF-CLI-18 step 2
              amended by record, both texts kept. `R-A`'s own principle governs it , never « plus rien
              à envoyer » on the one screen where he has something to send , and her rejection past the
              send is exactly that screen. `complete` below is the DOOR's count, which counts a rejected
              pièce as missing, so the card falls to the fourth copy and stops contradicting the row. */}
          {cash
            ? t("client.dossier.copieComptant")
            : completion !== null
              ? t("client.dossier.copieComplement")
              : sent && complete
                ? t("client.dossier.copieTransmis")
                : waitingForSeller
                  ? t("client.dossier.copieAttenteVendeur")
                  : complete
                    ? t("client.dossier.copieComplet")
                    : t("client.dossier.copieAttente")}
        </p>
        {/* Step 7: the control appears only when the visible list is complete
            AND the state is exactly « pieces » , the api's `collecte`. */}
        {waitingForSeller ? (
          <p
            className="mt-1 rounded-md bg-indigo-50 px-3 py-2 text-sm text-indigo-700"
            data-testid="dossier-en-attente-vendeur"
          >
            {t("client.dossier.attenteVendeur")}
          </p>
        ) : requestOpen ? null : (
          sendButton(false)
        )}
        {refusal === null ? null : (
          <p role="alert" className="text-xs text-rose-700">
            {refusal}
          </p>
        )}
      </Card>

      {/* Step 3: the chargé's card. The name is the door's (`P1-DOS-010`); the
          two contact controls are greyed with their round , the chargé's
          number reaches the client with the messaging round, not before. */}
      <Card shape="client" className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
            {initialsOf(dossier.ownerName ?? null)}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900" data-testid="dossier-charge">
              {dossier.ownerName ?? t("client.dossier.chargeAbsent")}
            </p>
            <p className="text-xs text-gray-500">{t("client.dossier.chargeRole")}</p>
          </div>
        </div>
        {/* `SWEEP-AA` (D-1019-2): no « WhatsApp » / « Appeler » until a door exists , a control without one is not drawn. */}
      </Card>

      {/* Step 4: the vehicle's pièces, read only, answered by the door beside the buyer's. */}
      <Card shape="client" className="flex flex-col gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-900">{t("client.dossier.piecesVehicule")}</p>
          <p className="mt-0.5 text-xs text-gray-500">{t("client.dossier.piecesVehiculeResume")}</p>
        </div>
        {documents === null ? (
          <p className="text-sm text-gray-500">-</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {documents.vehicleDocuments.map((piece) => (
              <li
                key={piece.code}
                className="flex items-start gap-2.5 py-2.5"
                data-testid={`dossier-vehicule-${piece.code}`}
              >
                {piece.received ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden="true" />
                ) : piece.notApplicable === true ? (
                  <MinusCircle className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                ) : (
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                )}
                <div className="flex-1">
                  <p className="text-sm leading-snug text-gray-900">
                    {t(VEHICLE_LABEL.get(piece.code) ?? "client.dossier.vehiculeExpertise")}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {piece.code === "expertise"
                      ? t("client.dossier.vehiculeParLExpert")
                      : t("client.dossier.vehiculeParLeVendeur")}
                    {piece.received
                      ? ""
                      : piece.notApplicable === true
                        ? ` · ${t("client.dossier.vehiculeSansObjet")}`
                        : ` · ${t("client.dossier.vehiculeAttendu")}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Step 5: « Ce que vous payez à Hakili » , the figures are `payments`'
          and the states the doors', a dash where a door did not answer. */}
      <Card shape="client" className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-gray-900">{t("client.dossier.frais")}</p>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-sm text-gray-900">{t("client.dossier.fraisVerification")}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
              {t("client.dossier.fraisVerificationDetail")}
            </p>
          </div>
          <div className="shrink-0 text-right" data-testid="dossier-frais-verification">
            <p className="text-sm font-semibold tabular-nums">
              {fees?.verification === null || fees === null ? "-" : formatMoney(fees.verification.amount)}
            </p>
            {fees?.verification === null || fees === null ? null : (
              <span
                className={`text-xs font-medium ${fees.verification.settled ? "text-emerald-700" : "text-amber-700"}`}
              >
                {fees.verification.settled ? t("client.dossier.regle") : t("client.dossier.aRegler")}
              </span>
            )}
          </div>
        </div>
        {cash ? null : (
          <>
            <div className="flex items-start justify-between gap-3 border-t border-gray-50 pt-3">
              <div className="flex-1">
                <p className="text-sm text-gray-900">{t("client.dossier.fraisDossier")}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                  {t("client.dossier.fraisDossierDetail")}
                </p>
              </div>
              <div className="shrink-0 text-right" data-testid="dossier-frais-dossier">
                <p className="text-sm font-semibold tabular-nums">
                  {fees?.file === null || fees?.file === undefined ? "-" : formatMoney(fees.file.amount)}
                </p>
                {fees?.file === null || fees?.file === undefined ? null : (
                  <span
                    className={`text-xs ${fees.file.status === "regle" ? "font-medium text-emerald-700" : "text-gray-400"}`}
                  >
                    {t(FILE_FEE_STATUS[fees.file.status])}
                  </span>
                )}
              </div>
            </div>
            <p className="rounded-xl bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-400">
              {t("client.dossier.fraisTaeg")}
            </p>
          </>
        )}
      </Card>

      {/* Step 6: « Vos pièces », one row per document the DOOR says he owes. */}
      <div>
        <h2 className="mb-2.5 text-sm font-semibold text-gray-900">{t("client.dossier.pieces")}</h2>
        {/**
         * `FX-2c`, `M1` (`F4`): **the cut-off, said ONCE for the card.**
         *
         * It was on every row first, and the pair of frame `71` showed what that is: five identical
         * sentences down one card, each squeezing its own row's label into a column. `round F3`'s
         * `W5` is the founder's own complaint about exactly this shape , « two greyed buttons each
         * trailing a sentence of prose » , and five is that complaint multiplied.
         *
         * So the sentence sits here, above the list, where it is true of every row at once; each
         * greyed control keeps it as its `title` for the pointer that asks. `R81`: nothing at all
         * while the file is still his, because a card that explains a gesture nobody is refused is a
         * card teaching its reader to skip the prose.
         */}
        {sent && pieces.some((piece) => piece.status === "recue") ? (
          <p
            className="mb-2.5 text-xs leading-relaxed text-gray-500"
            data-testid="dossier-pieces-remplacer-ferme"
          >
            {t("client.dossier.remplacerFerme")}
          </p>
        ) : null}
        {documents === null ? (
          <p className="text-sm text-gray-500" data-testid="dossier-pieces-sans-porte">
            {t("client.dossier.piecesSansPorte")}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {pieces.map((piece) => (
              <DocumentRow key={piece.code} piece={piece} sent={sent} deposit={deposit} />
            ))}
          </ul>
        )}
      </div>

      <Button variant="secondary" onClick={deconnecter}>
        {t("client.dossier.deconnexion")}
      </Button>
    </div>
  );
}

/** « AS » for Aminata Sow; a dash for the file nobody is seated on. */
const initialsOf = (name: string | null): string => {
  if (name === null || name.trim() === "") return "-";
  return name
    .split(/\s+/u)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};
